// A lead's conversation, on its page: every email both ways, from Ops and from
// Gmail, oldest first, like a thread in Gmail (owner, 2026-11-03: "it still shows
// up in our Gmail, but we also see the history of what they responded with, what
// we sent"). Reply answers the newest message in the same thread; New email
// starts one. Their words are shown as text, and the quoted tail of a reply is
// folded away until asked for.

import { useState, type ReactNode } from 'react'
import { api } from '@/api'
import type { ConversationMessage, Lead, LeadConversation as Thread } from '@/api'
import { Badge, Button, Card, CardHeader, ErrorState, RelativeTime, SkeletonRows, useToast } from '@/components/ui'
import { cn, formatDateTime } from '@/lib/format'
import { errorMessage } from '@/api/client'
import type { ReplyTo } from '@/lib/conversation'
import { invalidateQueries, useQuery } from '@/lib/query'
import { LeadEmailDialog } from './LeadEmail'

/** Splits off what a reply quotes: "On … wrote:" and the ">" lines after it. */
function splitQuoted(text: string): { own: string; quoted: string } {
  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  const at = lines.findIndex((l, i) => /^On .{4,200}wrote:\s*$/.test(l.trim()) || (/^>/.test(l) && lines.slice(i).every((x) => !x.trim() || /^>/.test(x))))
  if (at <= 0) return { own: text.trim(), quoted: '' }
  return { own: lines.slice(0, at).join('\n').trim(), quoted: lines.slice(at).join('\n').trim() }
}

function who(m: ConversationMessage, leadName: string): string {
  if (m.direction === 'in') return m.from.replace(/\s*<[^>]+>$/, '') || leadName
  return m.signer_name ? `${m.signer_name} (ScoreLM)` : 'ScoreLM'
}

function Labels({ m }: { m: ConversationMessage }) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      {m.campaign ? <Badge tone="neutral">{`${m.campaign.name} · ${m.campaign.step_number === 1 ? 'first email' : `follow-up ${m.campaign.step_number - 1}`}`}</Badge> : null}
      {m.from_gmail ? <Badge tone="outline">From Gmail</Badge> : null}
      {m.status === 'dry_run' ? <Badge tone="warn">Dry run: not sent</Badge> : null}
      {m.status === 'failed' ? <Badge tone="bad">Didn't go</Badge> : null}
      {m.status === 'scheduled' ? <Badge tone="neutral">Scheduled</Badge> : null}
      {m.replied ? <Badge tone="good">Replied</Badge> : null}
      {m.bounced ? <Badge tone="bad">Bounced</Badge> : null}
      {m.unsubscribed ? <Badge tone="outline">Unsubscribed</Badge> : null}
      {m.source === 'snippet' ? <Badge tone="outline">First words only</Badge> : null}
    </span>
  )
}

function Message({ m, leadName, onReply }: { m: ConversationMessage; leadName: string; onReply: (r: ReplyTo) => void }) {
  const [showQuoted, setShowQuoted] = useState(false)
  const { own, quoted } = splitQuoted(m.text)
  const theirs = m.direction === 'in'
  return (
    <li className={cn('grid gap-2 rounded-lg border p-3 sm:p-4', theirs ? 'border-accent/30 bg-accent-soft/20' : 'border-line bg-panel', theirs ? 'sm:mr-10' : 'sm:ml-10')}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="grid min-w-0 gap-0.5">
          <span className="text-[13.5px] font-semibold text-ink">
            {who(m, leadName)}
            <span className="ml-2 text-xs font-normal text-muted">{theirs ? 'wrote' : m.status === 'scheduled' ? 'will send' : 'sent'}</span>
          </span>
          {m.subject ? <span className="break-words text-xs text-muted">{m.subject}</span> : null}
        </div>
        <span className="text-xs text-muted" title={formatDateTime(m.at)}>
          <RelativeTime value={m.at} />
        </span>
      </div>
      <Labels m={m} />
      <p className="whitespace-pre-wrap break-words text-[13.5px] text-ink">{own || '(no text)'}</p>
      {quoted ? (
        <div>
          <button type="button" className="text-xs font-semibold text-muted hover:text-ink" aria-expanded={showQuoted} onClick={() => setShowQuoted((v) => !v)}>
            {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
          </button>
          {showQuoted ? <p className="mt-1 whitespace-pre-wrap break-words border-l-2 border-line pl-3 text-xs text-muted">{quoted}</p> : null}
        </div>
      ) : null}
      {m.message_id ? (
        <div>
          <Button size="sm" variant="ghost" aria-label={`Reply to this email from ${who(m, leadName)}`} onClick={() => onReply({ message_id: m.message_id!, subject: m.subject })}>
            Reply
          </Button>
        </div>
      ) : null}
    </li>
  )
}

/**
 * One person's conversation: a lead's, an inquiry's. `lead` is how the person is
 * named; `first` is a message that isn't an email (an inquiry's website message),
 * shown first; `compose` draws the Email or Reply dialog.
 */
export function ConversationCard({ who, email, queryKey, load, canEmail, first, compose, headerActions = true }: {
  who: string
  email: string | null
  queryKey: readonly unknown[]
  load: (signal?: AbortSignal) => Promise<Thread>
  canEmail: boolean
  first?: ConversationMessage | null
  compose: (reply: ReplyTo | null, close: () => void) => ReactNode
  /** Reply and New email in the card's header; off where the page's header already has a Reply. */
  headerActions?: boolean
}) {
  const { data, error, status, refetch } = useQuery(queryKey, (signal) => load(signal))
  const [composing, setComposing] = useState<{ reply: ReplyTo | null } | null>(null)
  const [syncing, setSyncing] = useState(false)
  const toast = useToast()
  const messages = [...(first ? [first] : []), ...(data?.messages ?? [])]

  const refresh = async () => {
    if (!email) return
    setSyncing(true)
    try {
      const r = await api.growth.leads.syncConversation(email)
      invalidateQueries('growth')
      refetch()
      toast.success(r.added ? `Found ${r.added} ${r.added === 1 ? 'email' : 'emails'} in Gmail.` : 'Up to date with Gmail.')
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setSyncing(false)
    }
  }
  const last = [...messages].reverse().find((m) => m.message_id)

  return (
    <Card>
      <CardHeader
        title="Conversation"
        description={data?.mailbox.configured ? (
          <>
            Every email with {email ?? 'them'}, from Ops and from Gmail. Read from Gmail <RelativeTime value={data.mailbox.polled_at} />.
          </>
        ) : (
          'Every email with them from Ops. Their replies and what you write in Gmail show here once the mailbox is set up on the server.'
        )}
        actions={
          <span className="flex gap-1.5">
            {data?.mailbox.configured && email ? (
              <Button size="sm" variant="ghost" loading={syncing} onClick={() => void refresh()}>
                Refresh
              </Button>
            ) : null}
            {canEmail && headerActions && last ? (
              <Button size="sm" variant="primary" onClick={() => setComposing({ reply: { message_id: last.message_id!, subject: last.subject } })}>
                Reply
              </Button>
            ) : null}
            {canEmail && headerActions ? (
              <Button size="sm" onClick={() => setComposing({ reply: null })}>
                New email
              </Button>
            ) : null}
          </span>
        }
      />
      {data?.mailbox.error ? <p className="mb-3 text-xs font-semibold text-bad">{data.mailbox.error}</p> : null}
      {status === 'loading' && !data ? (
        <SkeletonRows rows={3} label="Loading the conversation" />
      ) : status === 'error' && !data ? (
        <ErrorState error={error} title="Couldn't load the conversation" onRetry={refetch} />
      ) : messages.length === 0 ? (
        <p className="text-[13px] text-muted">No emails with them yet.</p>
      ) : (
        <ol aria-label={`Conversation with ${who}`} className="grid gap-3">
          {messages.map((m) => (
            <Message key={m.key} m={m} leadName={who} onReply={(reply) => setComposing({ reply })} />
          ))}
        </ol>
      )}
      {composing ? compose(composing.reply, () => setComposing(null)) : null}
    </Card>
  )
}

export function LeadConversation({ lead, canEmail }: { lead: Pick<Lead, 'id' | 'name' | 'email'>; canEmail: boolean }) {
  return (
    <ConversationCard
      who={lead.name}
      email={lead.email}
      queryKey={['growth', 'lead-conversation', lead.id]}
      load={(signal) => api.growth.leads.conversation(lead.id, signal)}
      canEmail={canEmail}
      compose={(reply, close) => <LeadEmailDialog lead={lead} open reply={reply} onClose={close} />}
    />
  )
}
